import {Box, Divider, Typography} from '@mui/material';
import {t} from 'i18next';
import {useState} from 'react';
import AurButton from '../../../common/aur-button/AurButton';
import AurIconButton from '../../../common/aur-button/AurIconButton';
import AurIcon, {IconNames} from '../../../common/aur-icon/AurIcon';
import AurPopup from '../../../common/aur-popup/AurPopup';
import {useAnalytics} from '../../../../hooks/useAnalytics';
import {
  GA_CLICK_TYPES,
  GA_EVENT_NAMES,
  GA_REGIONS,
  GA_SUBPAGE_TYPE,
} from '../../../../constants/analyticsConstants';

type HeaderProp = {
  onTrashClick?: () => void;
  saveAsDraftHandler: () => void;
  isEditFlow: boolean;
  onRunAllTermClick: () => void;
  shouldShowRunPendingButton: boolean;
  onRunAllPendingTermsClick: () => void;
};

export type STRCreateReviewDataType = {
  name: string;
  index: string;
  notes: string;
  type: string;
  settings: string[];
  relationalGroups: string[];
  emailRecipients: string[];
  searchableSet: string;
};
interface ReviewSearchProps {
  reviewData: STRCreateReviewDataType;
  saveAsDraftHandler: () => void;
  deleteSTR: () => void;
  isEditFlow: boolean;
  onRunAllTermClick: () => void;
  shouldShowRunPendingButton: boolean;
  onRunAllPendingTermsClick: () => void;
}

const RenderHeader = ({
  onTrashClick,
  saveAsDraftHandler,
  isEditFlow,
  onRunAllTermClick,
  shouldShowRunPendingButton,
  onRunAllPendingTermsClick,
}: HeaderProp) => {
  const {trackGAEvent} = useAnalytics();

  const stringPath = 'eca.searchTermReport.reviewSearch.';
  return (
    <Box className="header">
      <Typography className="typo-text headline3_size title-text" tabIndex={0}>
        {t(stringPath + 'header')}
      </Typography>
      <Box className="header-button-group">
        <AurButton
          type={'continue'}
          label={t(stringPath + 'runAllTerms')}
          size="Medium"
          classNames="header-button"
          onClick={() => {
            onRunAllTermClick();
            trackGAEvent({
              eventName: GA_EVENT_NAMES.CLICK,
              click: {
                name: t('gaTracking.str.runAllTerm'),
                type: GA_CLICK_TYPES.BUTTON,
                region: GA_REGIONS.TAB,
                action: t('gaTracking.str.runAllTermAction'),
              },
              subPage: {
                subpage_name: isEditFlow
                  ? t('gaTracking.str.steps.summary.edit')
                  : t('gaTracking.str.steps.summary.create'),
                subpage_type: GA_SUBPAGE_TYPE.TAB,
              },
            });
          }}
          dataTestId="runAllTerms"
        />
        {shouldShowRunPendingButton && isEditFlow && (
          <AurButton
            type={'continue'}
            label={t(stringPath + 'runPendingTerms')}
            size="Medium"
            classNames="header-button"
            onClick={() => {
              onRunAllPendingTermsClick();
              trackGAEvent({
                eventName: GA_EVENT_NAMES.CLICK,
                click: {
                  name: t('gaTracking.str.runPendingTerm'),
                  type: GA_CLICK_TYPES.BUTTON,
                  region: GA_REGIONS.TAB,
                  action: t('gaTracking.str.runPendingTermAction'),
                },
                subPage: {
                  subpage_name: t('gaTracking.str.steps.summary.edit'),
                  subpage_type: GA_SUBPAGE_TYPE.TAB,
                },
              });
            }}
            dataTestId="runPendingTerms"
          />
        )}
        {!isEditFlow && (
          <>
            <Divider
              orientation="vertical"
              variant="middle"
              flexItem
              className="divider"
            />

            <AurButton
              type={'exit'}
              label={t(stringPath + 'saveAsDraft')}
              size="Medium"
              classNames="header-button"
              onClick={() => {
                saveAsDraftHandler();
                trackGAEvent({
                  eventName: GA_EVENT_NAMES.CLICK,
                  click: {
                    name: t('gaTracking.str.saveAsDraft'),
                    type: GA_CLICK_TYPES.BUTTON,
                    region: GA_REGIONS.TAB,
                    action: t('gaTracking.str.saveAsDraftAction'),
                  },
                  subPage: {
                    subpage_name: t('gaTracking.str.steps.summary.create'),
                    subpage_type: GA_SUBPAGE_TYPE.TAB,
                  },
                });
              }}
              dataTestId="saveAsDraft"
            />

            <AurIconButton
              onClick={() => {
                onTrashClick && onTrashClick();
                trackGAEvent({
                  eventName: GA_EVENT_NAMES.CLICK,
                  click: {
                    name: t('gaTracking.global.delete'),
                    type: GA_CLICK_TYPES.BUTTON,
                    region: GA_REGIONS.TAB,
                    action: t('gaTracking.str.deleteAction'),
                  },
                  subPage: {
                    subpage_name: t('gaTracking.str.steps.summary.create'),
                    subpage_type: GA_SUBPAGE_TYPE.TAB,
                  },
                });
                trackGAEvent({
                  eventName: GA_EVENT_NAMES.SUB_PAGE_VIEW,
                  subPage: {
                    subpage_name: t('gaTracking.str.deleteActionModal'),
                    subpage_type: GA_SUBPAGE_TYPE.MODAL,
                  },
                });
              }}
              shape
              size="big"
              icon={<AurIcon icon={IconNames.Remove} />}
              dataTestId="deleteReport"
              ariaLabel={t(stringPath + 'delete')}
            />
          </>
        )}
      </Box>
    </Box>
  );
};
const ReviewSearch = (props: ReviewSearchProps) => {
  const {
    reviewData,
    saveAsDraftHandler,
    deleteSTR,
    isEditFlow = false,
    onRunAllTermClick,
    shouldShowRunPendingButton,
    onRunAllPendingTermsClick,
  } = props;
  const {trackGAEvent} = useAnalytics();
  const stringPath = 'eca.searchTermReport.reviewSearch.';
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const modalOkHandler = () => {
    setShowConfirmationModal(false);
    deleteSTR();
    trackGAEvent({
      eventName: GA_EVENT_NAMES.DELETE,
      click: {
        name: t('gaTracking.global.ok'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.str.deleteOkAction'),
      },
      subPage: {
        subpage_name: t('gaTracking.str.deleteActionModal'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };
  const modalCancelHandler = () => {
    setShowConfirmationModal(false);
    trackGAEvent({
      eventName: GA_EVENT_NAMES.CLICK,
      click: {
        name: t('gaTracking.global.cancel'),
        type: GA_CLICK_TYPES.BUTTON,
        region: GA_REGIONS.MODAL,
        action: t('gaTracking.str.deleteStrCancelAction'),
      },
      subPage: {
        subpage_name: t('gaTracking.str.deleteActionModal'),
        subpage_type: GA_SUBPAGE_TYPE.MODAL,
      },
    });
  };

  const deleteIconHandler = () => {
    setShowConfirmationModal(true);
  };

  const renderData = (
    key: string,
    type: 'Array' | 'Single',
    value?: string,
    valueArray?: string[]
  ) => {
    const isArray = type === 'Array';
    return (
      <Box className="key-value-main-div" key={key}>
        <Typography
          className="typo-text subhead4_size key-text"
          tabIndex={0}
        >
          {key}
        </Typography>
        {isArray ? (
          valueArray?.map((group, index) => (
            <Typography
              key={index + 1}
              className="typo-text body3_size value-text"
              tabIndex={0}
            >
              {group}
            </Typography>
          ))
        ) : (
          <Typography
            className="typo-text body3_size value-text text-wrap"
            tabIndex={0}
          >
            {value}
          </Typography>
        )}
      </Box>
    );
  };
  return (
    <Box className="reviewSearch-main">
      <RenderHeader
        onTrashClick={deleteIconHandler}
        saveAsDraftHandler={saveAsDraftHandler}
        isEditFlow={isEditFlow}
        onRunAllTermClick={onRunAllTermClick}
        shouldShowRunPendingButton={shouldShowRunPendingButton}
        onRunAllPendingTermsClick={onRunAllPendingTermsClick}
      />
      <Box className="first-section">
        <Box className="row-one">
          {renderData(t(stringPath + 'name'), 'Single', reviewData.name)}
          {renderData(t(stringPath + 'index'), 'Single', reviewData.index)}
          {renderData(
            t(stringPath + 'searchableSet'),
            'Single',
            reviewData.searchableSet
          )}
        </Box>
        <Box className="row-two">
          {renderData(t(stringPath + 'notes'), 'Single', reviewData.notes)}
        </Box>
      </Box>
      <Divider
        orientation="horizontal"
        variant="middle"
        flexItem
        className="horizontal-divider"
      />
      <Box className="second-section">
        <Box className="row-one">
          {renderData(t(stringPath + 'type'), 'Single', reviewData.type)}
          {renderData(
            t(stringPath + 'settings'),
            'Array',
            '',
            reviewData.settings
          )}
          {renderData(
            t(stringPath + 'includeRelationalGroup'),
            'Array',
            '',
            reviewData.relationalGroups
          )}
        </Box>
      </Box>
      <Divider
        orientation="horizontal"
        variant="middle"
        flexItem
        className="horizontal-divider"
      />
      <Box className="third-section">
        <Box className="row-one">
          {renderData(
            t(stringPath + 'emailNotificationRecipients'),
            'Array',
            '',
            reviewData.emailRecipients
          )}
        </Box>
      </Box>
      <AurPopup
        onOk={modalOkHandler}
        onCancel={modalCancelHandler}
        open={showConfirmationModal}
        showPopupTitle={true}
        popupTitle={t(stringPath + 'unSavedChangesModal.popupTitle')}
        showCloseIcon={true}
        showPopupHeading={true}
        popupHeading={t(stringPath + 'unSavedChangesModal.popupHeading')}
        contentType="text"
        popupContent={t(stringPath + 'unSavedChangesModal.popupContent')}
        labelActionCancel={t(stringPath + 'cancel')}
        labelActionOk={t(stringPath + 'ok')}
        className="aur-unsaved-changes-popup"
      />
    </Box>
  );
};

export default ReviewSearch;
